/**
 * Contact us as a window of the app's own (2026-11-01, the owner: "a window that opens without
 * mentioning the email, where people can have a subject and [a message] limited to like 250
 * characters ... do not mention my email directly"). The server emails it on (POST /api/v1/contact,
 * apps/api/src/contact.ts); no address of ours is on any page.
 *
 * openContact() opens it from anywhere - the footer, sign-in, a locked account; one ContactHost,
 * at the top of the app (main.tsx), draws it. /contact shows the same form as a page.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from './api.ts';
import { useSession } from './auth-client.ts';
import { recallEmail } from './pages/auth.tsx';
import { navigate } from './router.tsx';
import { t } from './strings.ts';

/** The server's limits (contact.ts). */
export const SUBJECT_MAX = 80;
export const MESSAGE_MAX = 250;

let openWindow: ((subject: string) => void) | null = null;

/** Open the Contact us window, with a subject already in it if given. Without a host, the page. */
export function openContact(subject = '') {
  if (openWindow) openWindow(subject);
  else navigate('/contact');
}

/** "Contact us", as a button that opens the window. */
export function ContactButton({ subject, className = 'button small', label }: { subject?: string; className?: string; label?: string }) {
  return <button type="button" className={className} onClick={() => openContact(subject)}>{label ?? t('contactUs')}</button>;
}

export function ContactHost() {
  const [subject, setSubject] = useState<string | null>(null);
  useEffect(() => {
    openWindow = setSubject;
    return () => { openWindow = null; };
  }, []);
  useEffect(() => {
    if (subject === null) return;
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setSubject(null); };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [subject]);
  if (subject === null) return null;
  // no closing on a click outside: a stray click would lose what was written
  return (
    <div className="confirm-veil no-print" role="presentation">
      <div className="confirm-dialog contact-dialog" role="dialog" aria-modal="true" aria-labelledby="contact-title">
        <h2 id="contact-title">{t('contactTitle')}</h2>
        <ContactForm subject={subject} onClose={() => setSubject(null)} />
      </div>
    </div>
  );
}

/** The form itself: the window's, and the /contact page's (no onClose there). */
export function ContactForm({ subject: first = '', onClose }: { subject?: string; onClose?: () => void }) {
  const { data: session } = useSession();
  const [email, setEmail] = useState(() => session?.user.email || recallEmail());
  const [subject, setSubject] = useState(first);
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [state, setState] = useState<{ busy?: boolean; error?: string; sent?: string }>({});
  const firstField = useRef<HTMLInputElement>(null);
  useEffect(() => { if (session?.user.email && !email) setEmail(session.user.email); }, [session?.user.email]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { firstField.current?.focus(); }, []);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    setState({ busy: true });
    try {
      await api.contact({ email: email.trim(), subject: subject.trim(), message: message.trim(), website });
      setState({ sent: email.trim() });
    } catch (err) {
      setState({ error: (err as Error).message || t('somethingWrong') });
    }
  };

  if (state.sent) {
    return (
      <div className="stack">
        <p className="ok" role="status">{t('contactSent', { email: state.sent })}</p>
        {onClose && <div className="confirm-buttons"><button type="button" className="primary" onClick={onClose}>{t('close')}</button></div>}
      </div>
    );
  }
  return (
    <form className="stack contact-form" onSubmit={send}>
      <p className="muted">{t('contactBody')}</p>
      <label>{t('contactYourEmail')}
        <input ref={firstField} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" maxLength={254} required />
      </label>
      <label>{t('contactSubject')}
        <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={SUBJECT_MAX} required />
      </label>
      <label>{t('contactMessage')}
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={MESSAGE_MAX} rows={5} required />
        <span className="hint contact-count" aria-live="polite">{t('contactCount', { n: message.length, max: MESSAGE_MAX })}</span>
      </label>
      {/* only a robot fills this in (hidden from people and screen readers) */}
      <input className="contact-trap" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} />
      {state.error && <p className="error" role="alert">{state.error}</p>}
      <div className="confirm-buttons">
        {onClose && <button type="button" onClick={onClose}>{t('cancel')}</button>}
        <button className="primary" disabled={state.busy || !email.trim() || !subject.trim() || !message.trim()}>{t('contactSend')}</button>
      </div>
    </form>
  );
}
